import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { getIsFetching } from '@/reducers/fetch'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { isNil } from 'lodash'
import {
  useFetchResponse,
  useEndDateForFinancialInsights,
  useReselector,
} from '@/utils/sharedHooks'
import { Text, Badge, Loader } from '@/ui'
import { CurrentInfoCard } from './CurrentInfoCard'
import { CurrentSummaryCard } from './CurrentSummaryCard'
import {
  INSIGHTS_CHANGE_TYPE,
  getInsightsMonthYear,
  INSIGHTS_TYPE,
  formatSummary,
  insightsCopyConstants,
  generateSampleSummaryExpenseData,
} from './utils'
import {
  fetchInsightsSummary,
  FETCH_INSIGHTS_SUMMARY_KEY,
} from './financeActions'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
import { TypographyStyles } from '@/styles'

export const CurrentPanel = ({
  inputDate,
  insightsType,
  shouldDisplaySampleData = false,
}: {
  inputDate?: string
  insightsType: INSIGHTS_TYPE
  shouldDisplaySampleData?: boolean
}) => {
  const currentDate = inputDate
    ? DateTime.fromFormat(inputDate, DATE_FORMATS_LUXON.INPUT)
    : DateTime.now()

  const endDate = useEndDateForFinancialInsights(currentDate)

  const { month, year } = useMemo(() => {
    return getInsightsMonthYear(endDate)
  }, [endDate])

  const summaryDataResponse = useFetchResponse(
    fetchInsightsSummary,
    insightsType,
    month,
    year
  )

  const isResponseEmpty =
    isNil(summaryDataResponse) || summaryDataResponse?.length === 0

  const summaryData = shouldDisplaySampleData
    ? generateSampleSummaryExpenseData[0]
    : summaryDataResponse?.[0]

  const changeType = useMemo(() => {
    if (!summaryData) return INSIGHTS_CHANGE_TYPE.EMPTY
    const {
      percent_change_from_previous_month_decimal,
      previous_month_total_in_cents,
      target_month_total_in_cents,
    } = summaryData
    if (previous_month_total_in_cents === 0) {
      return INSIGHTS_CHANGE_TYPE.LAST_MONTH_EMPTY
    } else if (target_month_total_in_cents === 0) {
      return INSIGHTS_CHANGE_TYPE.EMPTY
    } else if (percent_change_from_previous_month_decimal > 0) {
      return INSIGHTS_CHANGE_TYPE.INCREASE
    } else if (percent_change_from_previous_month_decimal < 0) {
      return INSIGHTS_CHANGE_TYPE.DECREASE
    }
    return INSIGHTS_CHANGE_TYPE.SAME
  }, [summaryData])

  const formattedData = useMemo(() => {
    return formatSummary(summaryData, insightsType)
  }, [summaryData, insightsType])

  const insightsFetchKey = FETCH_INSIGHTS_SUMMARY_KEY(insightsType, month, year)

  const queryLoading = useReselector(getIsFetching, insightsFetchKey)

  return (
    <>
      {shouldDisplaySampleData && (
        <Text variant={TypographyStyles.H2} className="w-full mb-3">
          <Badge color="orange" className="rounded-md">
            Sample data
          </Badge>
        </Text>
      )}

      <FreeTrialUnreviewedAlert />

      {queryLoading ? (
        <div className="w-full">
          <Loader active />
        </div>
      ) : (
        <div className="w-full flex flex-col md:flex-row flex-wrap gap-4">
          {/* Month Total Card */}
          <div className="w-full md:flex-2 md:min-w-fit">
            <CurrentInfoCard
              headerText={
                isResponseEmpty ? '$0.00' : formattedData?.month_total
              }
              subheader={formattedData?.month_name}
              bottomLeftText={
                formattedData?.monthly_average && 'Monthly average'
              }
              bottomRightText={
                isResponseEmpty ? '$0.00' : formattedData?.monthly_average
              }
              insightsFetchKey={insightsFetchKey}
            />
          </div>

          <div className="w-full md:flex-2 md:min-w-fit">
            <CurrentInfoCard
              headerText={isResponseEmpty ? '$0.00' : formattedData?.year_total}
              subheader={`Through ${formattedData?.month_name} ${year}`}
              bottomLeftText="Total"
              insightsFetchKey={insightsFetchKey}
            />
          </div>

          <div className="w-full md:flex-1 md:min-w-[280px]">
            <CurrentSummaryCard
              headerText={insightsCopyConstants[insightsType].label}
              insightsType={insightsType}
              type={changeType}
              currentMonth={formattedData?.month_name}
              prevMonth={formattedData?.previous_month_name}
              amount={formattedData?.month_total}
              percentChange={formattedData?.percent_change}
              percentCounterpart={formattedData?.percent_counterpart_change}
              isResponseEmpty={isResponseEmpty}
              prevMonthTotal={formattedData?.previous_month_total}
              insightsFetchKey={insightsFetchKey}
            />
          </div>
        </div>
      )}
    </>
  )
}
